import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterViewInit, ChangeDetectionStrategy, Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import ClassicEditor from 'src/assets/js/ckeditor';
import { Subject } from 'rxjs';
@Component({
    selector: 'wfdss-rte-mat-form-field',
    templateUrl: './rte-mat-form-field.component.html',
    styleUrls: ['./rte-mat-form-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: RTEMatFormFieldComponent
        }
    ],
    host: {
        '[id]': 'id',
    },
    standalone: false
})
  /**
   * @author Matt Messer 
   * @description Angular Forms compatible CKE5 based rich text editor. Formcontrol, formgroup and mat-form-field are identical to OEM Material form components.
   * @reference To understand how this component integrates with angular forms, follow this doc from the angular guides: https://v14.material.angular.io/guide/creating-a-custom-form-field-control#implementing-the-methods-and-properties-of-matformfieldcontrol */ 
export class RTEMatFormFieldComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {

  @ViewChild("editorPlaceholder", {static: true}) editorPlaceholder: ElementRef
  public editor : ClassicEditor
  /** @description Stores initial(parent FormControl) data until editor is created.  */
  public initializationEditorData
  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }
  updateErrorState(){
    this.touched = this.ngControl.touched
    const oldState = this.errorState
    const newState = (this.ngControl.errors && this.ngControl.touched)
    if(oldState != newState){
      this.errorState = newState
      this.stateChanges.next()
    }
  }
  // ====BEGIN CVA Properties====
  onChange = (editorData) => {}   // Placeholder function to prevent null errors 
  onTouched = () => {} // Placeholder function to prevent null errors
  touched = false;
  // ====END CVA Properties====
  constructor(  
    @Optional() @Self() public ngControl: NgControl,
    private _elementRef: ElementRef<HTMLElement>,
    private _focusMonitor: FocusMonitor,) 
  { 
    if(this.ngControl != null){
      this.ngControl.valueAccessor = this
    }
  }
  //===== BEGIN MatFormField interface implementation
  stateChanges: Subject<void> = new Subject<void>(); // MFF uses onPush change detection, this subject notifies parent of changes.
  get value(): string | null {
    if(this.editor){
      return this.editor.getData()
    }
    return null
  };
  set value(content: string | null) {
    if(content && this.editor){this.editor.setData(content)}
    this.stateChanges.next()
  } 
  static nextId = 0;
  @HostBinding() id = `${RTEMatFormFieldComponent.nextId++}`;
  private _placeholder: string;
  @Input() get placeholder(){
    return this._placeholder;
  }
  set placeholder(plh){
    this._placeholder = plh
    this.stateChanges.next()
  }
  focused: boolean = false;
  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }
  
  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }
  get empty(){
    if(this.editor){
      if(this.editor?.getData()){
        return true
      }
    }
    return false
  } 
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return true // Always float label. Collides with CKE window otherwise
  }

  private _required = false;
  @Input() get required() {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  
  private _disabled = false;
  @Input() get disabled(): boolean { return this._disabled; }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    if(this.editor){
      this._disabled ?  this.editor.enableReadOnlyMode("abc") : this.editor.disableReadOnlyMode("abc")
    } 
    this.stateChanges.next();
  }

  errorState: boolean;
  controlType?: string = "cke-rich-text";
  autofilled?: boolean;
  // userAriaDescribedBy?: string;
  @Input('aria-describedby') userAriaDescribedBy: string;
  setDescribedByIds(ids: string[]): void {
    // TODO: Do an accessability assessment.
    if(!ids){return}
    // const controlElement = this._elementRef.nativeElement
    // .querySelector('.example-tel-input-container')!;
    // controlElement.setAttribute('aria-describedby', ids.join(' '));
  }
  onContainerClick(event: MouseEvent) {
    // Not currently implemented, may deprecate
    // console.log((event.target as Element).tagName.toLowerCase() )
    // let focus = (event.target as Element).tagName.toLowerCase() != 'input' && (event.target as Element).tagName.toLowerCase() != 'input' 
  }
  //===== END MatFormField interface implementation
  //===== BEGIN ControlValueAcessor Interface implementation
  writeValue(newData: any): void {
    let safeString // sending null is a valid use case, but the editor can only deal with strings 
    if( (newData==null || newData == undefined)){
      safeString = ""
    } else {
      safeString = new String(newData).toString()
    } 
    if (this.editor){
      this.editor.setData(safeString)
    } else {
      this.initializationEditorData = safeString // On first load, editor has not yet initialized. This saves init data to pass to Editor.create()
    }
    this.stateChanges.next()
  }   

  registerOnChange(onChange: ()=>{}): void { //Runs once on init. Registers callback
    this.onChange = onChange
  }
  registerOnTouched(onTouched: ()=>{}): void {//Runs once on init. Registers callback
    this.onTouched = onTouched
  }
  setDisabledState?(isDisabled: boolean): void {
    // TODO: 'cache' this like initialzationEditorData
    this.disabled = isDisabled
    if(!this.editor){
      return
    }
    if(this.disabled){
      this.editor.enableReadOnlyMode("abc")
    } else {
      this.editor.disableReadOnlyMode("abc")
    }
    this.stateChanges.next()
  }
  //===== END ControlValueAcessor Interface implementation
  /**  @description this binds our specified CKE5 events to Mat-Form-Control and ControlValueAccessor interface properties. */
  integrateEditorIntoControlValueAccessorInterface(){
    // Callback runs when editor data is changed
    this.editor.model.document.on( 'change:data', (event) => {
      this.onChange(this.editor.getData())
      if(this.editor.getData()){ // <fg>.reset() will pass null. Don't mark as touched
        this.markAsTouched()
      }
    })
    // Callback runs on editor focus change
    this.editor.editing.view.document.on( 'change:isFocused', ( evt, data, nextFocusState ) => {
      if (nextFocusState === false){ // mark as touched when user leaves the editor 
        if(this.focused === true){
          this.markAsTouched()
        }
      }
      this.focused = nextFocusState});
  }  
  markAsTouched() {
    this.onTouched();
    this.touched = true;
    this.stateChanges.next()
  }

  ngOnInit(): void {}
  ngAfterViewInit(): void {
    ClassicEditor
      .create((this.editorPlaceholder.nativeElement as HTMLElement)
      // Toolbar options are defined by our custom build(as well as the plugins to run these toolbar options)
      // Code below is a good example of how to override the toolbar. This was a temporary workaround for font issues on bulleted lists, 
      // but if anyone needs to disable a toolbar in the future, this is the way. 
      // NOTE: Adding additional toolbar options probably requires tweaking and rebuilding the CKE5 build, as toolbar options only work if the underlying plugin is included in the build.   
      // ,{
      //   toolbar: [
      //     "undo", "redo",
      //     '|', "heading",
      //     '|', "bold", "italic","underline",
      //     '|', "numberedList",
      //     //"bulletedList",
      //     '|',
      //     "uploadImage",
      //     "fontSize",
      //     "fontColor",
      //     "fontBackgroundColor",
      //     '|',
      //     "alignment",
      //     "outdent",
      //     "indent",
      //     '|',
      //     "insertTable",
      //     "tableColumn", 
      //     "tableRow", 
      //     "mergeTableCells",
      //   ]
      // }
  )
    .then( editor => {
        this.editor = editor
        if(this.initializationEditorData){ //Editor must be instantiated before setting initial data
          this.editor.setData(this.initializationEditorData)
        }
        this.integrateEditorIntoControlValueAccessorInterface()
        // console.log( 'CKE5 Editor initialized successfully', editor );
        this.disabled = this.disabled // Editor must be instantiated before disabling it.
    } )
    .catch( err => {
        console.error( "CKE5 Error: Editor instance failed to initialize", err );
    } );
  }
  ngOnDestroy(): void {
    this.editor.destroy()
    this.stateChanges.complete()
  }
}